import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Wallet } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { t, tServer } from '@/i18n'

/**
 * Stands between a cashier and the till until the drawer is opened for the shift.
 *
 * A till opens **empty**. Typing an opening float here was money appearing from nowhere: the
 * drawer gained a hundred dollars that no safe, no owner and no earlier shift had given up, so
 * the shop's own record of its cash grew every time somebody started a shift. It also sat behind
 * no permission at all, so a cashier could declare any float they liked and the count at close
 * would agree with it.
 *
 * Money now reaches a drawer the way it does in the shop: out of the safe, by someone allowed to
 * open the safe, recorded as a movement with both ends written together.
 */
export function OpenShiftGate({
  registerId,
  registerName,
  onOpened,
}: {
  registerId: number
  registerName: string
  onOpened: () => void
}) {
  const [error, setError] = useState<string | null>(null)

  const open = useMutation({
    mutationFn: () =>
      apiClient.post('/api/shifts/open', {
        registerId,
        openingFloatUsd: 0,
        openingFloatLbp: 0,
      }),
    onSuccess: onOpened,
    onError: (caught) =>
      setError(
        caught instanceof ApiError
          ? tServer(caught.detail ?? caught.message)
          : t('Could not open the shift.'),
      ),
  })

  return (
    <div className="pb-page flex min-h-[70dvh] items-center justify-center px-4 pt-6">
      <div className="surface-raised w-full max-w-sm rounded-[var(--radius-card)] border p-6">
        <div className="surface-sunken mx-auto flex h-14 w-14 items-center justify-center rounded-2xl">
          <Wallet className="h-6 w-6 text-[var(--accent)]" />
        </div>

        <h1 className="mt-4 text-center text-lg font-semibold">{t('Open the till')}</h1>
        <p className="mt-1 text-center text-sm text-[var(--text-secondary)]">
          {t('{register} starts empty. Everything that goes through it from here is counted at close.', { register: registerName })}
        </p>

        <div className="surface-sunken mt-5 rounded-lg p-3 text-sm text-[var(--text-secondary)]">
          {t('Need change in the drawer? Once the till is open, someone who can reach the safe draws the float from it — Cash, then Drawer, then Draw change from the safe. That way every note in the till came from somewhere the shop can point at.')}
        </div>

        {error && (
          <p
            role="alert"
            className="mt-4 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500"
          >
            {error}
          </p>
        )}

        <Button
          size="lg"
          full
          className="mt-5"
          onClick={() => open.mutate()}
          disabled={open.isPending}
        >
          {open.isPending ? t('Opening…') : t('Start selling')}
        </Button>
      </div>
    </div>
  )
}
